import {
  createDecisionRecommendation,
  DECISION_ENGINES,
  DECISION_SCOPES,
  DECISION_TYPES,
} from "../models";
import { executiveRecommendationAdapter } from "./executiveRecommendationAdapter";

const RANGE_UNAVAILABLE = new Set([
  "POLICY_NOT_CONFIGURED",
  "APPETITE_NOT_REACHED",
  "LOWER_BOUND_POLICY_UNAVAILABLE",
  "FRONTIER_UNAVAILABLE",
]);

const firstDefined = (...values) => values.find((value) => value != null);

const positionCapital = (position) => {
  const explicit = firstDefined(
    position?.capitalEnvelope,
    position?.selectedCapitalEnvelope
  );
  if (explicit != null) return explicit;
  if (position?.actualCapital == null) return null;
  return Number(position.actualCapital) + Number(firstDefined(
    position.unusedCapital,
    position.unusedBudget,
    0
  ));
};

function availableBudget(portfolioData, recommendation) {
  return firstDefined(
    portfolioData?.availableBudget,
    portfolioData?.available_budget,
    portfolioData?.portfolioBudget,
    portfolioData?.portfolio_budget,
    portfolioData?.summary?.portfolio_budget,
    recommendation?.executive_position?.portfolio_budget
  );
}

function affordability(position, budget) {
  const requiredCapital = positionCapital(position);
  const actualCapitalUsed = firstDefined(
    position?.actualCapitalUsed,
    position?.actualCapital,
    null
  );
  const unusedCapital = firstDefined(
    position?.unusedCapital,
    position?.unusedBudget,
    requiredCapital != null && actualCapitalUsed != null
      ? Number(requiredCapital) - Number(actualCapitalUsed)
      : null
  );
  const hasBudget = budget != null && Number.isFinite(Number(budget));
  const hasRequirement = requiredCapital != null &&
    Number.isFinite(Number(requiredCapital));
  const budgetGap = hasBudget && hasRequirement
    ? Math.max(0, Number(requiredCapital) - Number(budget))
    : null;

  return {
    requiredCapital,
    actualCapitalUsed,
    unusedCapital,
    budgetGap,
    affordabilityStatus: !hasBudget || !hasRequirement
      ? "NOT_DETERMINED"
      : budgetGap === 0 ? "AFFORDABLE" : "NOT_AFFORDABLE",
  };
}

function executiveAlternative({ id, title, decisionType, position, budget,
  policy, sourceAnalyticalPosition }) {
  if (!position) return null;
  return {
    id: position.id ?? id,
    title,
    strategy: position.strategy ?? position.strategyType ?? decisionType,
    decisionType,
    capitalEnvelope: positionCapital(position),
    ...affordability(position, budget),
    scenarioAllocation: firstDefined(
      position.optimizedScenarioAllocation,
      position.scenarioAllocation,
      position.selectedPositions,
      []
    ),
    expectedEal: firstDefined(position.expectedEal, position.expectedLoss?.baseline),
    residualEal: firstDefined(position.residualEal, position.expectedLoss?.selected),
    p95: firstDefined(position.p95, position.tailRisk?.baselineP95),
    residualP95: firstDefined(position.residualP95, position.tailRisk?.selectedP95),
    capitalAtRisk: firstDefined(position.capitalAtRiskBaseline, position.capitalAtRisk?.baseline),
    residualCapitalAtRisk: firstDefined(position.residualCapitalAtRisk, position.capitalAtRisk?.selected),
    appetiteConsequence: firstDefined(position.appetiteConsequence, position.appetiteStatus),
    marginalEfficiency: firstDefined(
      position.marginalEfficiency,
      position.efficiency?.marginalAnnualEalEfficiency
    ),
    policyProvenance: position.policyProvenance ?? policy ?? null,
    candidateReferences: firstDefined(position.candidateReferences,
      position.allocationReferences, position.selectedPositions, []),
    sourceAnalyticalPosition,
  };
}

/** Form decision alternatives only from discrete, backend-modeled positions. */
export function capitalEfficiencyDecisionEvidence(portfolioData, recommendation) {
  const frontier = portfolioData?.capitalEfficiencyFrontier;
  const range = frontier?.capitalEfficientRange ?? frontier?.efficientRange;
  const status = range?.status ?? "FRONTIER_UNAVAILABLE";
  const budget = availableBudget(portfolioData, recommendation);
  const evidence = {
    status,
    availableBudget: budget ?? null,
    efficientRange: range ?? null,
    appetiteAchievement: frontier?.appetiteAchievement ?? null,
    selectedAnalyticalPosition: null,
    maximumModeledProtection: frontier?.maximumModeledProtection ?? null,
    alternatives: [],
    noAffordableEfficientPosition: false,
  };

  if (status !== "AVAILABLE" || RANGE_UNAVAILABLE.has(status)) return evidence;

  const selectedEnvelope = firstDefined(
    portfolioData.selectedCapitalEnvelope,
    portfolioData.selected_capital_envelope
  );
  const selected = firstDefined(
    range.selectedPosition,
    range.currentPosition,
    frontier.selectedPosition,
    portfolioData.selectedAnalyticalPosition,
    portfolioData.selected_analytical_position,
    selectedEnvelope == null ? null : (range.positions ?? []).find((position) =>
      Number(positionCapital(position)) === Number(selectedEnvelope)
    )
  );
  evidence.selectedAnalyticalPosition = selected ?? null;

  const specifications = [
    ["minimum-efficient", "Minimum Efficient", "MINIMUM_EFFICIENT", range.lowerBound],
    ["capital-efficient", "Capital Efficient", "CAPITAL_EFFICIENT", selected],
    ["upper-efficient", "Upper Efficient", "UPPER_EFFICIENT", range.upperBound],
    ["maximum-modeled-protection", "Maximum Modeled Protection", "MAXIMUM_MODELED_PROTECTION", frontier.maximumModeledProtection],
  ];
  const seen = new Set();
  evidence.alternatives = specifications.flatMap(([id, title, type, position]) => {
    if (!position) return [];
    const identity = `${type}:${position.id ?? position.actualCapital ?? id}`;
    if (seen.has(identity)) return [];
    seen.add(identity);
    return [executiveAlternative({ id, title, decisionType: type, position,
      budget, policy: range.policy, sourceAnalyticalPosition: position })];
  });
  const efficient = evidence.alternatives.filter((item) =>
    item.decisionType !== "MAXIMUM_MODELED_PROTECTION");
  evidence.noAffordableEfficientPosition = budget != null && efficient.length > 0 &&
    efficient.every((item) => item.affordabilityStatus === "NOT_AFFORDABLE");
  return evidence;
}

export function portfolioRecommendationAdapter(portfolioData) {
  const mode = portfolioData?.portfolioAnalysisOptimizationMode ??
    portfolioData?.portfolio_analysis_optimization_mode ?? "LEGACY";
  const optimization = portfolioData?.combinedBundleOptimization ??
    portfolioData?.combined_bundle_optimization ?? null;
  const authoritative = optimization?.executiveRecommendation ??
    optimization?.executive_recommendation ?? null;
  const capitalRecommendation = executiveRecommendationAdapter(authoritative);

  if (mode === "CAPITAL_ALLOCATION") {
    return capitalRecommendation ? [capitalRecommendation] : [];
  }

  if (mode === "SHOW_BOTH") {
    const legacy = legacyPortfolioRecommendationAdapter(portfolioData);
    return capitalRecommendation ? [...legacy, capitalRecommendation] : legacy;
  }

  // An additive optimizer response may arrive before the mode flag is wired.
  if (capitalRecommendation) return [capitalRecommendation];
  return legacyPortfolioRecommendationAdapter(portfolioData);
}

function legacyPortfolioRecommendationAdapter(portfolioData) {
  const recommendation = portfolioData?.executive_recommendation;

  if (
    !recommendation ||
    portfolioData?.recommendation_valid === false ||
    ["FAILED", "INFEASIBLE"].includes(
      String(portfolioData?.status || "").toUpperCase()
    )
  ) {
    return [];
  }

  const capitalEfficiency = capitalEfficiencyDecisionEvidence(
    portfolioData,
    recommendation
  );
  const existingAlternatives = portfolioData?.alternatives ??
    recommendation.alternatives ?? [];
  const executiveAlternatives = [
    ...existingAlternatives,
    ...capitalEfficiency.alternatives,
  ];

  return [createDecisionRecommendation({
    id: recommendation.id ?? portfolioData.id ?? "PORTFOLIO-001",
    source: "portfolio_decision",
    engine: DECISION_ENGINES.PORTFOLIO,
    scope: DECISION_SCOPES.PORTFOLIO,
    decisionType:
      recommendation.decision_type ??
      DECISION_TYPES.REALLOCATE_CAPITAL,
    title: recommendation.headline ?? "Portfolio Capital Allocation",
    strategy:
      recommendation.strategy ??
      recommendation.executive_position ??
      null,
    rationale:
      recommendation.rationale ??
      recommendation.observations ??
      null,
    financialImpact: {
      capitalRequired:
        portfolioData?.summary?.total_funding_requirement ?? null,
      expectedAnnualLossReduction:
        portfolioData?.metrics?.eal?.reduction ?? null,
      capitalAtRiskP95Reduction:
        portfolioData?.metrics?.p95?.reduction ?? null,
    },
    evidence: {
      alternatives: executiveAlternatives,
      selectedActions:
        portfolioData?.selected_actions ??
        recommendation.selected_actions ??
        [],
      capitalEfficiency,
    },
    metadata: {
      mode: "LEGACY",
      sourceRecommendation: recommendation,
      businessQuestion:
        recommendation.businessQuestion ?? recommendation.business_question ??
        portfolioData?.businessQuestion ?? portfolioData?.business_question ?? null,
      decisionRequired:
        recommendation.decisionRequired ?? recommendation.decision_required ??
        portfolioData?.decisionRequired ?? portfolioData?.decision_required ?? null,
      recommendedAlternative:
        recommendation.recommendedAlternative ?? recommendation.recommended_alternative ??
        recommendation.strategy ?? recommendation.executive_position ?? null,
      alternatives: executiveAlternatives,
      sourceReference:
        recommendation.sourceReference ?? recommendation.source_reference ??
        portfolioData?.sourceReference ?? portfolioData?.source_reference ?? null,
    },
  })];
}
